import { hideModal, modalBodyType } from '@/redux/navigationSlice';
import { selectModalData } from '@/redux/selectors';
import { type IconBadgeTone, type MaterialIconType, Modal } from '@repo/ui';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Lazy-load modal body components to reduce initial bundle
const CancelNewOrderBodyLazy = dynamic(
  () => import('./cancelNewOrderBody').then((mod) => ({ default: mod.CancelNewOrderBody })),
  { loading: () => null },
);

const CancelSuccessFullOrderBodyLazy = dynamic(
  () => import('./cancelSuccessfullOrderBody').then((mod) => ({ default: mod.CancelSuccessFullOrderBody })),
  { loading: () => null },
);

const CloseSessionBodyLazy = dynamic(
  () => import('./closeSessionBody').then((mod) => ({ default: mod.CloseSessionBody })),
  { loading: () => null },
);

const OrderDetailsBodyLazy = dynamic(
  () => import('./orderDetailsBody').then((mod) => ({ default: mod.OrderDetailsBody })),
  {
    loading: () => null,
  },
);

const OrderFunnelBodyLazy = dynamic(
  () => import('./orderFunnelBody').then((mod) => ({ default: mod.OrderFunnelBody })),
  {
    loading: () => null,
  },
);

const SelectedPlanBodyLazy = dynamic(
  () => import('./selectedPlanBody').then((mod) => ({ default: mod.SelectedPlanBody })),
  {
    loading: () => null,
  },
);

const SmsSentBodyLazy = dynamic(() => import('./smsSentBody').then((mod) => ({ default: mod.SmsSentBody })), {
  loading: () => null,
});

const bodySelector = (body: modalBodyType) => {
  switch (body) {
    case 'cancelSuccessfullOrder':
      return <CancelSuccessFullOrderBodyLazy />;
    case 'cancelNewOrder':
      return <CancelNewOrderBodyLazy />;
    case 'selectedPlan':
      return <SelectedPlanBodyLazy />;
    case 'smsSent':
      return <SmsSentBodyLazy />;
    case 'orderFunnel':
      return <OrderFunnelBodyLazy />;
    case 'orderDetails':
      return <OrderDetailsBodyLazy />;
    case 'closeSession':
      return <CloseSessionBodyLazy />;
    default:
      return <></>;
  }
};

// Dialog icons, same as customer-mobile: concentric rings above the title
const iconSelector = (body: modalBodyType): { icon: MaterialIconType; tone: IconBadgeTone } | null => {
  switch (body) {
    case 'closeSession':
      return { icon: 'logout', tone: 'warning' };
    case 'cancelNewOrder':
      return { icon: 'remove_shopping_cart', tone: 'warning' };
    case 'cancelSuccessfullOrder':
      return { icon: 'cancel', tone: 'error' };
    case 'smsSent':
      return { icon: 'sms', tone: 'brand' };
    case 'orderDetails':
      return { icon: 'receipt_long', tone: 'brand' };
    default:
      return null;
  }
};

/**
 * Renders a Modal component with title and body based on redux.
 *
 * @return {React.JSX.Element} The rendered Modal component.
 */
export const CommerceModal = () => {
  const modalData = useSelector(selectModalData);
  const dispatch = useDispatch();
  const { open, body, title, onClose } = modalData;
  const BodyComponent = bodySelector(body);
  const icon = iconSelector(body);

  // Memoized close handler to prevent re-creation on every render
  const handleClose = useCallback(() => {
    dispatch(hideModal());
    onClose?.();
  }, [dispatch, onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      heading={title}
      icon={icon}
      contentSx={{
        maxHeight: { xs: '100%', md: '32rem' },
        px: { xs: 0, md: 6 },
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {BodyComponent}
    </Modal>
  );
};
